import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import {
  MatchDTO,
  MatchToSave,
} from 'screens/liveScoring/DTO';
import { stores } from 'mobx-store';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

// Removed unused imports to clean up the component.
// import ImageUploader from 'screens/liveScoring/components/uploadLogo';
// import { TournamentDTO, SportDTO, GenderDto } from 'screens/liveScoring/DTO';

interface MatchFormProps {
  open: boolean;
  onClose: () => void;
  match: MatchDTO | null;
}

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

// The validation schema has been updated to match the flattened data structure.
// It now validates the numeric IDs directly.
const validationSchema = Yup.object().shape({
  teamHId: Yup.number().required('Home team is required').min(1, 'Home team is required'),
  teamAId: Yup.number().required('Away team is required').min(1, 'Away team is required'),
  tournamentId: Yup.number().required('Tournament is required').min(1, 'Tournament is required'),
  start_date: Yup.string().required('Start date is required'),
  genderId: Yup.number().required('Gender is required').min(1, 'Gender is required'),
  stadium: Yup.string().required('Stadium is required'),

});

const AddEditMatchModal: React.FC<MatchFormProps> = observer(
  ({ open, onClose, match }) => {
    const classes = useStyles();
    const { liveScoringStore } = stores;

    // Defines the initial values based on the 'match' prop.
    // If 'match' exists, it extracts the IDs directly.
    // Otherwise, it sets default values for a new match.
    const initialValues = match
      ? {
        teamHId: match.teamH.id,
        teamAId: match.teamA.id,
        tournamentId: match.tournament?.id || 0,
        start_date: match.start_date,
        genderId: match.gender.id,
        stadium: match.stadium,
      }
      : {
        teamHId: 0,
        teamAId: 0,
        tournamentId: 0,
        start_date: '',
        genderId: 0,
        actual_start_time: '',
        stadium: '',
      };

    const handleSubmit = async (values: typeof initialValues) => {
      try {
        const toSave: MatchToSave = {
          start_date: values.start_date,
          genderId: values.genderId,
          tournamentId: values.tournamentId,
          teamHId: values.teamHId,
          teamAId: values.teamAId,
          stadium: values.stadium,
        };

        if (match?.id) {
          await liveScoringStore.updateMatch(match.id, toSave);
        } else {
          await liveScoringStore.createMatch(toSave);
        }

        onClose();
      } catch (error) {
        console.error('Error submitting match form:', error);
      }
    };

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{match ? 'Edit Match' : 'Add New Match'}</DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                {/* Home Team Select Field */}
                <FormControl
                  className={classes.formControl}
                  error={touched.teamHId && !!errors.teamHId}
                >
                  <InputLabel id="teamH-label">Home Team</InputLabel>
                  <Select
                    labelId="teamH-label"
                    name="teamHId"
                    value={values.teamHId}
                    onChange={(e) => setFieldValue('teamHId', e.target.value)}
                  >
                    {liveScoringStore.teams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Away Team Select Field */}
                <FormControl
                  className={classes.formControl}
                  error={touched.teamAId && !!errors.teamAId}
                >
                  <InputLabel id="teamA-label">Away Team</InputLabel>
                  <Select
                    labelId="teamA-label"
                    name="teamAId"
                    value={values.teamAId}
                    onChange={(e) => setFieldValue('teamAId', e.target.value)}
                  >
                    {liveScoringStore.teams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Tournament Select Field */}
                <FormControl
                  className={classes.formControl}
                  error={touched.tournamentId && !!errors.tournamentId}
                >
                  <InputLabel id="torment-label">Tournament</InputLabel>
                  <Select
                    labelId="torment-label"
                    name="tournamentId"
                    value={values.tournamentId}
                    onChange={(e) => setFieldValue('tournamentId', e.target.value)}
                  >
                    {liveScoringStore.tournaments.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Start Date Field */}
                <DateTimePicker
                  label="Start Date and Time"
                  value={values.start_date}
                  onChange={(newValue) => {
                    setFieldValue('start_date', newValue);
                  }}
                  fullWidth
                  margin="normal"
                  // This library automatically renders a TextField, so you don't need a separate renderInput prop.
                  // If you need custom props for the TextField, you pass them directly to DateTimePicker.
                  inputProps={{
                    name: "start_datetime"
                  }}
                />

                {/* Gender Select Field */}
                <FormControl
                  className={classes.formControl}
                  error={touched.genderId && !!errors.genderId}
                >
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    name="genderId"
                    value={values.genderId}
                    onChange={(e) => setFieldValue('genderId', e.target.value)}
                  >
                    {liveScoringStore.genders.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Field
                  as={TextField}
                  name="stadium"
                  label="Stadium"
                  fullWidth
                  margin="normal"
                  error={touched.stadium && !!errors.stadium}
                  helperText={touched.stadium && errors.stadium}
                />
              </DialogContent>
              {/* Optional: Remove or keep this for debugging */}
              {/* <div>{JSON.stringify(errors, null, 2)}</div> */}
              <DialogActions>
                <Button onClick={onClose} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  },
);

export default AddEditMatchModal;